/**
 * A LÓGICA DO SELETOR (`CampoSelecao`), sem React e sem DOM.
 *
 * O seletor deixou de ser o `<select>` nativo em 20/10/2026 (pedido do dono:
 * "onde tenha foto, coloque a foto no dropdown e deixe ele mais moderno").
 * O nativo dava de graça o teclado e o leitor de tela; o próprio precisa
 * provar que não perdeu nada disso. Por isso a decisão de cada tecla mora
 * aqui, em função pura, e o teste a percorre sem navegador: o componente só
 * aplica o que esta função devolve.
 *
 * O padrão é o combobox de lista do WAI-ARIA (APG, "select-only combobox"):
 * - fechado: setas, Enter, Espaço, Home e End abrem; letra pula para a opção;
 * - aberto: setas andam, Home e End vão às pontas, Enter (e Espaço, sem busca)
 *   escolhe e fecha, Esc fecha e devolve o foco ao campo, Tab fecha e deixa o
 *   foco seguir — a lista nunca prende o Tab;
 * - com mais de `LIMITE_SEM_BUSCA` opções, a lista abre com um campo de busca
 *   e o foco vai para ele: digitar filtra.
 */

import type { OpcaoDeSelecao } from './Campo';

/** Até este número, a lista se percorre de olho; acima, ela abre com busca. */
export const LIMITE_SEM_BUSCA = 7;

/** A lista para de crescer em 320 px — oito linhas de 40 — e rola por dentro. */
export const ALTURA_MAXIMA_DA_LISTA = 320;

/** Quanto o PageUp e o PageDown andam: a altura visível da lista. */
const PASSO_DA_PAGINA = 8;

/** A opção que representa "nada escolhido" (o `rotuloVazio` de hoje) entra como a primeira da lista. */
export function itensDoSeletor(opcoes: readonly OpcaoDeSelecao[], rotuloVazio?: string): readonly OpcaoDeSelecao[] {
  return rotuloVazio ? [{ valor: '', rotulo: rotuloVazio }, ...opcoes] : opcoes;
}

export function temBusca(totalDeOpcoes: number): boolean {
  return totalDeOpcoes > LIMITE_SEM_BUSCA;
}

/** Sem acento e sem caixa: "maquina" acha "MÁQUINA". */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('pt-BR')
    .trim();
}

/**
 * Busca no rótulo, nas etiquetas e na segunda linha: o código, o código
 * interno, a placa, a descrição e a obra da máquina. Quem só sabe a placa
 * ("ABC1D23") acha a máquina pela placa.
 */
export function filtrarOpcoes(itens: readonly OpcaoDeSelecao[], termo: string): readonly OpcaoDeSelecao[] {
  const procurado = normalizar(termo);
  if (procurado === '') return itens;
  const palavras = procurado.split(/\s+/);
  return itens.filter((item) => {
    const etiquetas = (item.etiquetas ?? []).map((e) => e.texto).join(' ');
    const onde = normalizar(`${item.rotulo} ${etiquetas} ${item.detalhe ?? ''}`);
    return palavras.every((p) => onde.includes(p));
  });
}

/**
 * O TEXTO INTEIRO da opção, para o `title` (a dica do mouse) e o leitor de
 * tela: o rótulo, cada etiqueta com o que ela é ("Placa ABC1D23") e a segunda
 * linha. O que a lista corta por falta de largura continua lido aqui.
 */
export function textoInteiro(opcao: OpcaoDeSelecao): string {
  const etiquetas = (opcao.etiquetas ?? []).map((e) => (e.titulo ? `${e.titulo} ${e.texto}` : e.texto));
  return [opcao.rotulo, ...etiquetas, opcao.detalhe].filter((parte) => parte !== undefined && parte !== '').join(' · ');
}

/**
 * Digitar letras com a lista sem busca pula para a primeira opção que COMEÇA
 * com elas, como no `<select>` nativo. Uma letra só, repetida, anda entre as
 * opções daquela letra ("t", "t" vai do TRA-001 ao TRA-002).
 */
export function saltarPorLetras(itens: readonly OpcaoDeSelecao[], letras: string, desde: number): number {
  const procurado = normalizar(letras);
  if (procurado === '' || itens.length === 0) return desde;
  const repetida = [...procurado].every((c) => c === procurado[0]);
  const prefixo = repetida ? (procurado[0] ?? '') : procurado;
  /* Com uma letra (ou a mesma repetida), começa DEPOIS da atual; com várias,
     a atual ainda vale ("tr" depois de "t" fica no TRA-001). */
  const inicio = repetida ? desde + 1 : Math.max(desde, 0);
  for (let passo = 0; passo < itens.length; passo += 1) {
    const i = (inicio + passo) % itens.length;
    if (normalizar(itens[i]?.rotulo ?? '').startsWith(prefixo)) return i;
  }
  return desde;
}

export interface EstadoDoSeletor {
  readonly aberto: boolean;
  /** Índice da opção ativa NA LISTA FILTRADA; -1 quando não há nenhuma. */
  readonly ativo: number;
  readonly termo: string;
}

export const FECHADO: EstadoDoSeletor = { aberto: false, ativo: -1, termo: '' };

export interface ContextoDaTecla {
  /** As opções visíveis agora (já filtradas pela busca). */
  readonly itens: readonly OpcaoDeSelecao[];
  /** O valor escolhido hoje. */
  readonly valor: string;
  readonly busca: boolean;
}

export interface Resposta {
  readonly estado: EstadoDoSeletor;
  /** O valor a gravar, quando a tecla escolheu. */
  readonly escolher?: string;
  /** Para onde o foco vai depois da tecla. */
  readonly focar?: 'gatilho' | 'busca';
  /** A tecla foi usada aqui: o componente chama `preventDefault` (e o Esc não fecha a janela de trás). */
  readonly consumida: boolean;
}

const IGNORADA = (estado: EstadoDoSeletor): Resposta => ({ estado, consumida: false });

/** Onde a lista abre: na opção escolhida, ou na primeira. */
export function aoAbrir(contexto: ContextoDaTecla, onde: 'escolhida' | 'primeira' | 'ultima' = 'escolhida'): EstadoDoSeletor {
  const total = contexto.itens.length;
  if (total === 0) return { aberto: true, ativo: -1, termo: '' };
  if (onde === 'primeira') return { aberto: true, ativo: 0, termo: '' };
  if (onde === 'ultima') return { aberto: true, ativo: total - 1, termo: '' };
  const escolhida = contexto.itens.findIndex((i) => i.valor === contexto.valor);
  return { aberto: true, ativo: escolhida >= 0 ? escolhida : 0, termo: '' };
}

function limitar(indice: number, total: number): number {
  if (total === 0) return -1;
  return Math.min(Math.max(indice, 0), total - 1);
}

/**
 * A decisão de cada tecla. `origem` diz de onde ela veio: do campo (o gatilho)
 * ou do campo de busca dentro da lista aberta.
 */
export function teclar(
  estado: EstadoDoSeletor,
  tecla: { readonly key: string; readonly altKey?: boolean; readonly ctrlKey?: boolean; readonly metaKey?: boolean },
  contexto: ContextoDaTecla,
  origem: 'gatilho' | 'busca' = 'gatilho',
): Resposta {
  const { key } = tecla;
  const total = contexto.itens.length;

  if (!estado.aberto) {
    switch (key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        return { estado: aoAbrir(contexto), consumida: true, ...(contexto.busca ? { focar: 'busca' as const } : {}) };
      case 'Home':
        return { estado: aoAbrir(contexto, 'primeira'), consumida: true, ...(contexto.busca ? { focar: 'busca' as const } : {}) };
      case 'End':
        return { estado: aoAbrir(contexto, 'ultima'), consumida: true, ...(contexto.busca ? { focar: 'busca' as const } : {}) };
      default:
        return IGNORADA(estado);
    }
  }

  switch (key) {
    case 'Escape':
      return { estado: FECHADO, focar: 'gatilho', consumida: true };
    case 'Tab':
      /* Fecha sem escolher e deixa o Tab seguir: a lista não prende o foco. */
      return { estado: FECHADO, consumida: false };
    case 'ArrowDown':
      if (tecla.altKey) return IGNORADA(estado);
      return { estado: { ...estado, ativo: limitar(estado.ativo + 1, total) }, consumida: true };
    case 'ArrowUp':
      /* Alt + seta para cima escolhe e fecha (APG). */
      if (tecla.altKey) return escolherAtivo(estado, contexto);
      return { estado: { ...estado, ativo: limitar(estado.ativo - 1, total) }, consumida: true };
    case 'PageDown':
      return { estado: { ...estado, ativo: limitar(estado.ativo + PASSO_DA_PAGINA, total) }, consumida: true };
    case 'PageUp':
      return { estado: { ...estado, ativo: limitar(estado.ativo - PASSO_DA_PAGINA, total) }, consumida: true };
    case 'Home':
      return { estado: { ...estado, ativo: limitar(0, total) }, consumida: true };
    case 'End':
      return { estado: { ...estado, ativo: limitar(total - 1, total) }, consumida: true };
    case 'Enter':
      return escolherAtivo(estado, contexto);
    case ' ':
      /* Na busca, o espaço é parte do que se digita. */
      if (origem === 'busca') return IGNORADA(estado);
      return escolherAtivo(estado, contexto);
    default:
      return IGNORADA(estado);
  }
}

function escolherAtivo(estado: EstadoDoSeletor, contexto: ContextoDaTecla): Resposta {
  const item = contexto.itens[estado.ativo];
  /* Enter sem opção ativa (busca sem resultado) não fecha: a pessoa ainda está procurando. */
  if (!item) return { estado, consumida: true };
  return { estado: FECHADO, escolher: item.valor, focar: 'gatilho', consumida: true };
}

/** A tecla é uma letra (ou número) que a pessoa digitou, e não um atalho. */
export function eLetra(tecla: { readonly key: string; readonly altKey?: boolean; readonly ctrlKey?: boolean; readonly metaKey?: boolean }): boolean {
  return tecla.key.length === 1 && tecla.key !== ' ' && !tecla.ctrlKey && !tecla.metaKey && !tecla.altKey;
}

export interface LugarDaLista {
  /** `true`: a lista abre ACIMA do campo, porque embaixo não cabe (p.31). */
  readonly acima: boolean;
  /** Distância do topo da tela (abrindo embaixo) ou da base (abrindo em cima). */
  readonly distancia: number;
  readonly esquerda: number;
  readonly largura: number;
  readonly alturaMaxima: number;
}

/**
 * Colada ao campo, a 8 px; embaixo, ou em cima quando embaixo não cabe e em
 * cima cabe mais (p.31: "invertendo se não couber"). Na largura do campo, nunca
 * menos que o popover mínimo e nunca saindo da tela pela direita.
 */
export function lugarDaLista({
  campo,
  alturaDesejada,
  tela,
  afastamento = 8,
  margem = 8,
  larguraMinima = 200,
}: {
  readonly campo: { readonly top: number; readonly bottom: number; readonly left: number; readonly width: number };
  readonly alturaDesejada: number;
  readonly tela: { readonly largura: number; readonly altura: number };
  readonly afastamento?: number;
  readonly margem?: number;
  readonly larguraMinima?: number;
}): LugarDaLista {
  const desejada = Math.min(alturaDesejada, ALTURA_MAXIMA_DA_LISTA);
  const abaixo = tela.altura - campo.bottom - afastamento - margem;
  const acimaLivre = campo.top - afastamento - margem;
  const acima = abaixo < desejada && acimaLivre > abaixo;
  const espaco = Math.max(acima ? acimaLivre : abaixo, 0);
  const largura = Math.min(Math.max(campo.width, larguraMinima), tela.largura - 2 * margem);
  const esquerda = Math.max(margem, Math.min(campo.left, tela.largura - margem - largura));
  return {
    acima,
    distancia: acima ? tela.altura - campo.top + afastamento : campo.bottom + afastamento,
    esquerda,
    largura,
    alturaMaxima: Math.min(ALTURA_MAXIMA_DA_LISTA, espaco),
  };
}

/** As iniciais do avatar (p.24): a primeira letra do primeiro e do último nome. */
export function iniciaisDoNome(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  const primeira = partes[0]?.charAt(0) ?? '';
  const ultima = partes.length > 1 ? (partes.at(-1)?.charAt(0) ?? '') : '';
  return `${primeira}${ultima}`.toLocaleUpperCase('pt-BR');
}
